import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Plus, Search, MessageSquare, Trash2, Pencil, Send, Paperclip, X,
  Sparkles, Square, BookOpen, Settings as SettingsIcon, ChevronDown,
  Loader2, FileText, Check, HelpCircle,
} from "lucide-react";
import { api, API_BASE, errorMessage, tokenStore, orgStore } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import {
  Sheet, SheetTrigger, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { MessageBubble } from "@/components/MessageBubble";
import { toast } from "sonner";
import { ModuleGuide } from "@/components/ModuleGuide";
import { CHAT_GUIDE } from "@/lib/guides";
import { SamTipCard } from "@/components/SamTipCard";
import { useSamTip } from "@/context/SamTipsContext";

const STARTER_PROMPTS = [
  { title: "Draft an outbound email",  text: "Help me draft a cold email to a series-B SaaS CRO about pipeline acceleration. Keep it under 90 words and avoid sales clichés." },
  { title: "Summarise Q4 KPIs",        text: "Summarise these Q4 numbers for our leadership update: 3 new logos, 2 churned, 9.2% reply rate. Confident but honest tone." },
  { title: "Plan a content series",    text: "Outline a 4-part content series on 'AI for B2B sales operators'. Each post should be unique angle + a 1-sentence hook." },
  { title: "Explain in plain English", text: "Explain in plain English: what does retrieval-augmented generation actually do, and when shouldn't I use it?" },
];

// Iter 20 — module-scoped constants so the upload helper's useCallback deps
// stay stable (the previous in-component declaration was tripping the React
// hooks/exhaustive-deps lint rule).
const IMAGE_MIME_TYPES = ["image/png", "image/jpeg", "image/jpg", "image/webp", "image/gif"];
const TEXT_EXT_REGEX = /\.(pdf|docx|txt|md)$/i;
const IMAGE_EXT_REGEX = /\.(png|jpe?g|webp|gif)$/i;
const MAX_IMAGE_MB = 5;
const MAX_TEXT_MB = 20;

export default function Chat() {
  const { id: routeConvId } = useParams();
  const navigate = useNavigate();

  const [models, setModels] = useState([]);
  const [defaultModelId, setDefaultModelId] = useState("gpt-4o");
  const [convs, setConvs] = useState([]);
  const [active, setActive] = useState(null);         // active conversation object
  const [messages, setMessages] = useState([]);       // {id, role, content, model_id, file_ids, kb_chunk_ids_used, created_at}
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [draft, setDraft] = useState("");
  const [useKb, setUseKb] = useState(true);
  // Iter 22 (audit D2) — KB readiness gate. When the org has at least one
  // ready doc, we render "Grounded in your KB · N docs" + keep the use-kb
  // toggle ON by default. When empty, we surface an explicit upload CTA
  // so the empty state stops looking like generic chat.
  const [kbReadiness, setKbReadiness] = useState({ ready: false, ready_count: 0 });
  const [attachments, setAttachments] = useState([]); // {id, filename, size}
  const [streaming, setStreaming] = useState(false);
  const [streamingText, setStreamingText] = useState("");
  const [streamingMeta, setStreamingMeta] = useState(null);
  const [renameTarget, setRenameTarget] = useState(null);
  const [renameText, setRenameText] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [systemPromptOpen, setSystemPromptOpen] = useState(false);
  const [systemPromptDraft, setSystemPromptDraft] = useState("");
  const [tab, setTab] = useState("main");
  const tip = useSamTip("chat");
  const fileInputRef = useRef(null);
  const abortRef = useRef(null);
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  // Iter 20 — race-condition fix for Enter-to-send. Without a ref the
  // `send` useCallback closes over a stale `streaming` value, which the
  // SMB audit (Brunno César) flagged as "pressing Enter sometimes does
  // nothing right after the previous reply finishes". The ref is updated
  // synchronously alongside the state setter.
  const streamingRef = useRef(false);
  const setStreamingBoth = useCallback((v) => {
    streamingRef.current = v;
    setStreaming(v);
  }, []);

  // ---- Initial load ----
  useEffect(() => {
    // Iter 22 (audit D2) — fetch KB readiness in parallel with conversations.
    // If the org has no ready KB docs, the use_kb toggle is forced OFF and
    // the empty-state CTA renders below the composer.
    api.get("/kb/readiness").then((r) => setKbReadiness(r.data)).catch(() => setKbReadiness({ ready: false, ready_count: 0 }));
    (async () => {
      try {
        const [m, c] = await Promise.all([api.get("/chat/models"), api.get("/chat/conversations")]);
        setModels(m.data.models);
        setDefaultModelId(m.data.default);
        setConvs(c.data);
        if (routeConvId) {
          const found = c.data.find((x) => x.id === routeConvId);
          if (found) await openConversation(found);
        } else if (c.data.length > 0) {
          await openConversation(c.data[0]);
        }
      } catch (err) { toast.error(errorMessage(err)); }
      finally { setLoading(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-scroll on new messages / streaming tokens
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, streamingText]);

  // ---- Conversation helpers ----
  const openConversation = async (conv) => {
    setActive(conv);
    setSystemPromptDraft(conv.system_prompt || "");
    setAttachments([]);
    try {
      const r = await api.get(`/chat/conversations/${conv.id}/messages`);
      setMessages(r.data);
      navigate(`/app/chat/${conv.id}`, { replace: true });
    } catch (err) { toast.error(errorMessage(err)); }
  };

  const newConversation = async () => {
    try {
      const r = await api.post("/chat/conversations", { title: "New conversation", model_id: defaultModelId });
      setConvs((cs) => [r.data, ...cs]);
      await openConversation(r.data);
      setDraft("");
      textareaRef.current?.focus();
    } catch (err) { toast.error(errorMessage(err)); }
  };

  const renameConversation = async () => {
    if (!renameTarget || !renameText.trim()) return;
    try {
      const r = await api.patch(`/chat/conversations/${renameTarget.id}`, { title: renameText.trim() });
      setConvs((cs) => cs.map((c) => (c.id === renameTarget.id ? { ...c, title: r.data.title } : c)));
      if (active?.id === renameTarget.id) setActive({ ...active, title: r.data.title });
      setRenameTarget(null);
    } catch (err) { toast.error(errorMessage(err)); }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/chat/conversations/${deleteTarget.id}`);
      setConvs((cs) => cs.filter((c) => c.id !== deleteTarget.id));
      if (active?.id === deleteTarget.id) {
        setActive(null); setMessages([]);
        navigate("/app/chat", { replace: true });
      }
      setDeleteTarget(null);
      toast.success("Conversation deleted");
    } catch (err) { toast.error(errorMessage(err)); }
  };

  const changeModel = async (newModelId) => {
    if (!active || newModelId === active.model_id) return;
    try {
      const r = await api.patch(`/chat/conversations/${active.id}`, { model_id: newModelId });
      setActive(r.data);
      setConvs((cs) => cs.map((c) => (c.id === active.id ? { ...c, model_id: newModelId } : c)));
      toast.success(`Switched to ${models.find((m) => m.id === newModelId)?.display_name}`);
    } catch (err) { toast.error(errorMessage(err)); }
  };

  const saveSystemPrompt = async () => {
    if (!active) return;
    try {
      const r = await api.patch(`/chat/conversations/${active.id}`, { system_prompt: systemPromptDraft });
      setActive(r.data);
      setSystemPromptOpen(false);
      toast.success("System prompt saved");
    } catch (err) { toast.error(errorMessage(err)); }
  };

  // ---- File upload ----
  // Iter 20 — accepts images alongside PDFs/DOCX/TXT/MD. The audit
  // (Brunno César) flagged the previous picker as silently rejecting
  // pasted/folder-dropped images. We now route everything through a single
  // `uploadFile` helper that also powers paste + drag-drop.
  const uploadFile = useCallback(async (file) => {
    if (!file) return;
    const isImage = IMAGE_MIME_TYPES.includes(file.type) || IMAGE_EXT_REGEX.test(file.name || "");
    const isText = TEXT_EXT_REGEX.test(file.name || "");
    if (!isImage && !isText) {
      toast.error(`Unsupported file: ${file.name || "(no name)"} — use PDF, DOCX, TXT, MD or an image.`);
      return;
    }
    const capMB = isImage ? MAX_IMAGE_MB : MAX_TEXT_MB;
    if (file.size > capMB * 1024 * 1024) {
      toast.error(`File exceeds ${capMB}MB`);
      return;
    }
    const fd = new FormData();
    // Some pasted clipboard files have empty names — give them one.
    const safeName = (file.name && file.name.length) ? file.name : `pasted-${Date.now()}.${isImage ? "png" : "txt"}`;
    fd.append("file", file, safeName);
    if (active?.id) fd.append("conversation_id", active.id);
    try {
      const r = await fetch(`${API_BASE}/chat/upload`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${tokenStore.get()}`,
          "X-Org-Id": orgStore.get() || "",
        },
        body: fd,
      });
      if (!r.ok) {
        const t = await r.text();
        try { const j = JSON.parse(t); throw new Error(j.detail || "Upload failed"); }
        catch { throw new Error(t || "Upload failed"); }
      }
      const data = await r.json();
      setAttachments((a) => [...a, {
        id: data.id,
        filename: data.filename,
        size: data.size,
        kind: data.kind || (isImage ? "image" : "text"),
        image_data_url: data.image_data_url || null,
      }]);
      toast.success(`Attached: ${data.filename}`);
    } catch (err) { toast.error(err.message || "Upload failed"); }
  }, [active?.id]);

  const onFilePicked = async (e) => {
    const files = Array.from(e.target.files || []);
    for (const f of files) { await uploadFile(f); }
    e.target.value = "";
  };

  const onPaste = useCallback(async (e) => {
    const items = Array.from(e.clipboardData?.items || []);
    const fileItems = items.filter((it) => it.kind === "file");
    if (fileItems.length === 0) return;  // pure text paste — let the textarea handle it
    e.preventDefault();
    for (const it of fileItems) {
      const f = it.getAsFile();
      if (f) await uploadFile(f);
    }
  }, [uploadFile]);

  const [dropActive, setDropActive] = useState(false);
  const onDragOver = useCallback((e) => {
    if (Array.from(e.dataTransfer?.types || []).includes("Files")) {
      e.preventDefault();
      setDropActive(true);
    }
  }, []);
  const onDragLeave = useCallback(() => setDropActive(false), []);
  const onDrop = useCallback(async (e) => {
    e.preventDefault();
    setDropActive(false);
    const files = Array.from(e.dataTransfer?.files || []);
    // Folder drops produce DataTransferItemList entries with webkitGetAsEntry;
    // `dataTransfer.files` already contains the flattened files, so iterate.
    for (const f of files) { await uploadFile(f); }
  }, [uploadFile]);

  // ---- Streaming send ----
  const send = useCallback(async ({ regenerate = false } = {}) => {
    if (!active) {
      await newConversation();
      // The newConversation flow continues; user will press send again.
      return;
    }
    const text = regenerate ? null : draft.trim();
    if (!regenerate && !text) return;
    if (streamingRef.current) return;

    const fileIds = attachments.map((a) => a.id);
    if (!regenerate) {
      // Optimistic user message
      setMessages((ms) => [
        ...ms,
        { id: `pending-${Date.now()}`, role: "user", content: text, file_ids: fileIds, attached_files: attachments, created_at: new Date().toISOString() },
      ]);
      setDraft("");
      setAttachments([]);
    } else {
      // Drop last assistant from UI
      setMessages((ms) => {
        const lastAssistantIdx = [...ms].reverse().findIndex((m) => m.role === "assistant");
        if (lastAssistantIdx === -1) return ms;
        return ms.slice(0, ms.length - 1 - lastAssistantIdx);
      });
    }

    setStreamingBoth(true);
    setStreamingText("");
    setStreamingMeta(null);

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const res = await fetch(`${API_BASE}/chat/conversations/${active.id}/messages`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${tokenStore.get()}`,
          "X-Org-Id": orgStore.get() || "",
        },
        body: JSON.stringify({
          content: text || "",
          file_ids: fileIds,
          use_kb: useKb,
          regenerate,
        }),
        signal: controller.signal,
      });
      if (!res.ok) {
        const errTxt = await res.text();
        try { const j = JSON.parse(errTxt); throw new Error(j.detail || `HTTP ${res.status}`); }
        catch { throw new Error(errTxt || `HTTP ${res.status}`); }
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let finalAssistantId = null;
      let finalTitle = null;

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const events = buffer.split("\n\n");
        buffer = events.pop() || "";
        for (const evt of events) {
          const line = evt.trim();
          if (!line.startsWith("data:")) continue;
          try {
            const payload = JSON.parse(line.slice(5).trim());
            if (payload.type === "meta") {
              setStreamingMeta(payload);
              finalAssistantId = payload.assistant_id;
            } else if (payload.type === "token") {
              setStreamingText((t) => t + payload.content);
            } else if (payload.type === "done") {
              finalTitle = payload.title;
            } else if (payload.type === "error") {
              throw new Error(payload.error || "Stream error");
            }
          } catch (e) { /* ignore malformed lines */ }
        }
      }

      // Finalise: pull true messages from server (and refresh conv list to grab auto-title)
      const [msgsR, convsR] = await Promise.all([
        api.get(`/chat/conversations/${active.id}/messages`),
        api.get("/chat/conversations"),
      ]);
      setMessages(msgsR.data);
      setConvs(convsR.data);
      const updated = convsR.data.find((c) => c.id === active.id);
      if (updated) setActive(updated);
    } catch (err) {
      if (err.name === "AbortError") {
        toast("Generation stopped");
      } else {
        toast.error(err.message || "Streaming failed");
        // refresh messages to keep UI consistent
        try {
          const r = await api.get(`/chat/conversations/${active.id}/messages`);
          setMessages(r.data);
        } catch (_) {}
      }
    } finally {
      setStreamingBoth(false);
      setStreamingText("");
      setStreamingMeta(null);
      abortRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, draft, attachments, useKb]);

  const stop = () => abortRef.current?.abort();

  const onKeyDown = (e) => {
    // Iter 20 — guard against:
    //  1. IME composition (Chinese/Japanese/Korean input): the Enter that
    //     commits a character must NOT trigger send.
    //  2. Auto-repeat (key held down): one keystroke == one send attempt.
    //  3. The streaming race: if a reply is still arriving, swallow Enter
    //     silently rather than queueing a second turn that would error.
    if (e.key !== "Enter" || e.shiftKey) return;
    if (e.nativeEvent?.isComposing || e.keyCode === 229) return;
    if (e.repeat) return;
    e.preventDefault();
    if (streamingRef.current) return;
    send();
  };

  // ---- Filtering ----
  const filteredConvs = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return convs;
    return convs.filter((c) => c.title.toLowerCase().includes(q));
  }, [convs, search]);

  const activeModel = models.find((m) => m.id === active?.model_id) || models.find((m) => m.id === defaultModelId);

  return (
    <Tabs value={tab} onValueChange={setTab} className="flex h-[calc(100vh-7rem)] flex-col">
      <TabsList className="self-start bg-white/5 text-white" data-testid="chat-tabs">
        <TabsTrigger value="main" className="data-[state=active]:bg-white data-[state=active]:text-black" data-testid="chat-tab-main">
          <MessageSquare className="mr-1 h-3.5 w-3.5" /> Chat
        </TabsTrigger>
        <TabsTrigger value="guide" className="data-[state=active]:bg-white data-[state=active]:text-black" data-testid="chat-tab-guide">
          <HelpCircle className="mr-1 h-3.5 w-3.5" /> Guide
        </TabsTrigger>
      </TabsList>
      <TabsContent value="main" className="mt-3 flex flex-1 gap-4 overflow-hidden">
      {/* Conversation rail */}
      <aside className="hidden w-72 flex-col rounded-2xl border border-white/5 bg-white/[0.02] p-3 md:flex" data-testid="chat-conversation-rail">
        <Button
          onClick={newConversation}
          className="mb-3 w-full rounded-xl border border-white/10 bg-gradient-to-r from-indigo-500/20 via-violet-500/15 to-fuchsia-500/20 text-white hover:from-indigo-500/30 hover:to-fuchsia-500/30"
          data-testid="chat-new-conversation"
        >
          <Plus className="mr-2 h-4 w-4" /> New conversation
        </Button>

        <div className="relative mb-3">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-white/40" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search conversations"
            className="border-white/10 bg-black/40 pl-9 text-sm text-white placeholder:text-white/40"
            data-testid="chat-search"
          />
        </div>

        <div className="flex-1 space-y-1 overflow-y-auto pr-1 no-scrollbar">
          {filteredConvs.length === 0 && !loading && (
            <div className="px-2 py-6 text-center text-xs text-white/40">No conversations yet</div>
          )}
          {filteredConvs.map((c) => (
            <div
              key={c.id}
              onClick={() => openConversation(c)}
              className={`group flex cursor-pointer items-center gap-2 rounded-lg px-2.5 py-2 text-sm transition ${
                active?.id === c.id ? "bg-white/8 text-white" : "text-white/65 hover:bg-white/5 hover:text-white"
              }`}
              data-testid={`conv-row-${c.id}`}
            >
              <MessageSquare className="h-3.5 w-3.5 flex-shrink-0 text-white/45" />
              <span className="flex-1 truncate">{c.title}</span>
              <div className="flex opacity-0 transition group-hover:opacity-100">
                <button
                  onClick={(e) => { e.stopPropagation(); setRenameTarget(c); setRenameText(c.title); }}
                  className="rounded p-1 text-white/40 hover:bg-white/5 hover:text-white"
                  data-testid={`conv-rename-${c.id}`}
                ><Pencil className="h-3 w-3" /></button>
                <button
                  onClick={(e) => { e.stopPropagation(); setDeleteTarget(c); }}
                  className="rounded p-1 text-white/40 hover:bg-red-500/10 hover:text-red-300"
                  data-testid={`conv-delete-${c.id}`}
                ><Trash2 className="h-3 w-3" /></button>
              </div>
            </div>
          ))}
        </div>
      </aside>

      {/* Main panel */}
      <div className="flex flex-1 flex-col rounded-2xl border border-white/5 bg-[#0a0a0a]/40">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-white/5 px-5 py-3">
          <div className="flex min-w-0 items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-indigo-500/40 to-fuchsia-500/40">
              <Sparkles className="h-4 w-4 text-white" />
            </div>
            <div className="min-w-0">
              <div className="truncate font-display text-sm text-white">{active?.title || "AI Chat Assistant"}</div>
              <div className="text-xs text-white/40">{useKb ? "RAG enabled" : "RAG off"}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-xs text-white/80 transition hover:bg-white/10"
                  data-testid="chat-model-picker"
                >
                  {activeModel?.display_name || "Model"}
                  <ChevronDown className="h-3 w-3 text-white/50" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64 bg-popover">
                <DropdownMenuLabel className="text-xs text-muted-foreground">Model</DropdownMenuLabel>
                {models.map((m) => (
                  <DropdownMenuItem
                    key={m.id} onClick={() => changeModel(m.id)}
                    className="flex items-start gap-2"
                    data-testid={`model-option-${m.id}`}
                  >
                    <div className="flex-1">
                      <div className="text-sm">{m.display_name}</div>
                      <div className="text-xs text-muted-foreground">{m.provider} · {m.description}</div>
                    </div>
                    {active?.model_id === m.id && <Check className="mt-1 h-3.5 w-3.5 text-fuchsia-400" />}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <Sheet open={systemPromptOpen} onOpenChange={setSystemPromptOpen}>
              <SheetTrigger asChild>
                <button
                  disabled={!active}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-white/10 bg-white/5 text-white/70 transition hover:bg-white/10 disabled:opacity-40"
                  data-testid="chat-settings-button"
                >
                  <SettingsIcon className="h-3.5 w-3.5" />
                </button>
              </SheetTrigger>
              <SheetContent className="border-white/10 bg-[#0a0a0a] text-white">
                <SheetHeader>
                  <SheetTitle className="font-display text-2xl">Conversation settings</SheetTitle>
                  <SheetDescription className="text-white/55">
                    System prompts shape every reply from the assistant in this conversation.
                  </SheetDescription>
                </SheetHeader>
                <div className="mt-6 space-y-3">
                  <Label className="text-xs uppercase tracking-wider text-white/50">System prompt</Label>
                  <Textarea
                    value={systemPromptDraft}
                    onChange={(e) => setSystemPromptDraft(e.target.value)}
                    rows={10}
                    placeholder="You are a helpful assistant…"
                    className="border-white/10 bg-black/40 text-white"
                    data-testid="system-prompt-input"
                  />
                  <Button onClick={saveSystemPrompt} className="rounded-xl bg-white text-black hover:bg-zinc-200" data-testid="system-prompt-save">
                    Save
                  </Button>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-3 sm:px-6" data-testid="chat-messages-list">
          {loading && (
            <div className="flex h-full items-center justify-center text-white/40">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          )}

          {!loading && !active && (
            <>
              <div className="mx-auto mt-4 max-w-3xl">
                <SamTipCard
                  show={convs.length === 0}
                  enabled={tip.enabled}
                  message={tip.message}
                  prefill={tip.prefill}
                  testId="sam-tip-chat"
            module="chat"
                />
              </div>
              <EmptyHero onPrompt={(text) => { setDraft(text); textareaRef.current?.focus(); }} onNew={newConversation} />
            </>
          )}

          {active && messages.length === 0 && !streaming && (
            <EmptyHero conversationTitle={active.title} onPrompt={(text) => { setDraft(text); textareaRef.current?.focus(); }} />
          )}

          <div className="mx-auto max-w-3xl">
            {messages.map((m, i) => {
              const isLast = i === messages.length - 1 && m.role === "assistant" && !streaming;
              const modelName = models.find((mm) => mm.id === m.model_id)?.display_name;
              return (
                <MessageBubble
                  key={m.id}
                  // Iter 23 (audit D2) — Sources chip footer renders from
                  // `kb_indicator`. For persisted messages, the backend
                  // returns `kb_sources` on the MessagePublic shape.
                  message={{ ...m, kb_indicator: m.kb_sources || m.kb_indicator }}
                  isLast={isLast}
                  onRegenerate={isLast ? () => send({ regenerate: true }) : null}
                  modelDisplayName={modelName}
                />
              );
            })}

            {streaming && (
              <MessageBubble
                streaming
                message={{
                  id: "stream",
                  role: "assistant",
                  content: streamingText || "…",
                  model_id: streamingMeta?.model_id,
                  kb_indicator: streamingMeta?.kb_chunks,
                }}
                modelDisplayName={streamingMeta?.display_name}
              />
            )}

            <div ref={messagesEndRef} />
          </div>
        </div>

        {/* Composer */}
        <div className="border-t border-white/5 p-3 sm:p-4">
          <div className="mx-auto max-w-3xl">
            {attachments.length > 0 && (
              <div className="mb-2 flex flex-wrap gap-2" data-testid="chat-attachments">
                {attachments.map((a) => (
                  <span key={a.id} className="inline-flex items-center gap-1.5 rounded-md border border-white/10 bg-white/5 px-2 py-1 text-xs text-white/80">
                    {a.kind === "image" && a.image_data_url ? (
                      <img
                        src={a.image_data_url}
                        alt={a.filename}
                        className="h-6 w-6 rounded object-cover"
                        data-testid={`attachment-thumb-${a.id}`}
                      />
                    ) : (
                      <FileText className="h-3 w-3" />
                    )}
                    {a.filename}
                    <button
                      onClick={() => setAttachments((arr) => arr.filter((x) => x.id !== a.id))}
                      className="ml-1 text-white/45 hover:text-white"
                      data-testid={`attachment-remove-${a.id}`}
                    ><X className="h-3 w-3" /></button>
                  </span>
                ))}
              </div>
            )}

            <div
              className={`relative rounded-2xl border bg-white/[0.03] transition focus-within:border-white/20 ${
                dropActive ? "border-fuchsia-400/60 bg-fuchsia-500/10" : "border-white/10"
              }`}
              onDragOver={onDragOver}
              onDragLeave={onDragLeave}
              onDrop={onDrop}
              data-testid="chat-composer-dropzone"
            >
              <Textarea
                ref={textareaRef}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={onKeyDown}
                onPaste={onPaste}
                placeholder="Message Symplee — Enter to send, Shift+Enter for newline. Paste or drop images too."
                rows={1}
                className="min-h-[56px] max-h-48 resize-none border-0 bg-transparent px-4 py-4 pr-32 text-base text-white placeholder:text-white/40 focus-visible:ring-0 disabled:opacity-60"
                disabled={streaming}
                data-testid="chat-composer-input"
              />

              <div className="absolute bottom-2 right-2 flex items-center gap-1">
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept=".pdf,.docx,.txt,.md,.png,.jpg,.jpeg,.webp,.gif,image/*"
                  onChange={onFilePicked}
                  className="hidden"
                  data-testid="chat-file-input"
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={streaming}
                  className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-white/55 transition hover:bg-white/5 hover:text-white disabled:opacity-50"
                  title="Attach file (PDF/DOCX/TXT/MD)"
                  data-testid="chat-attach-button"
                >
                  <Paperclip className="h-4 w-4" />
                </button>
                {streaming ? (
                  <button
                    type="button" onClick={stop}
                    className="inline-flex h-9 items-center gap-1 rounded-lg bg-white px-3 text-sm font-medium text-black hover:bg-zinc-200"
                    data-testid="chat-stop-button"
                  >
                    <Square className="h-3.5 w-3.5 fill-current" /> Stop
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => send()}
                    disabled={!draft.trim() && !active?.id}
                    className="inline-flex h-9 items-center gap-1 rounded-lg brand-bg px-3 text-sm font-medium text-white shadow-lg shadow-fuchsia-500/20 disabled:opacity-40"
                    data-testid="chat-send-button"
                  >
                    Send <Send className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            </div>

            {/* Iter 22 (audit D2) — RAG-by-default affordance. When the
                KB has ≥1 ready doc, show the doc count so the user can
                trust their replies are grounded. When empty, surface a
                quiet CTA to upload the first doc. */}
            <div className="mt-2 flex items-center justify-between text-xs text-white/45">
              <label className="inline-flex items-center gap-2 cursor-pointer" data-testid="chat-rag-toggle-label">
                <Switch
                  checked={useKb && kbReadiness.ready}
                  onCheckedChange={setUseKb}
                  disabled={!kbReadiness.ready}
                  data-testid="chat-use-kb-toggle"
                />
                <BookOpen className="h-3 w-3 text-fuchsia-400" />
                Use knowledge base
              </label>
              {kbReadiness.ready ? (
                <span data-testid="chat-grounded-indicator">
                  {useKb
                    ? <>Grounded in your KB · <span className="text-white/70">{kbReadiness.ready_count} doc{kbReadiness.ready_count === 1 ? "" : "s"}</span></>
                    : "Generic mode — KB off for this thread"}
                </span>
              ) : (
                <Link
                  to="/app/knowledge"
                  className="text-fuchsia-300 hover:text-fuchsia-200"
                  data-testid="chat-kb-empty-cta"
                >
                  Upload a doc → grounded answers
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>
      </TabsContent>
      <TabsContent value="guide" className="mt-3 flex-1 overflow-y-auto">
        <ModuleGuide
          title={CHAT_GUIDE.title}
          whatItIs={CHAT_GUIDE.whatItIs}
          capabilities={CHAT_GUIDE.capabilities}
          howTo={CHAT_GUIDE.howTo}
          ctaLabel={CHAT_GUIDE.cta}
          onCta={() => setTab("main")}
        />
      </TabsContent>

      {/* Rename dialog */}
      <AlertDialog open={!!renameTarget} onOpenChange={(o) => !o && setRenameTarget(null)}>
        <AlertDialogContent className="border-white/10 bg-[#0a0a0a] text-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Rename conversation</AlertDialogTitle>
          </AlertDialogHeader>
          <Input
            value={renameText} onChange={(e) => setRenameText(e.target.value)}
            className="border-white/10 bg-black/40 text-white" autoFocus
            data-testid="rename-input"
          />
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-white/15 text-white hover:bg-white/5">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={renameConversation} className="bg-white text-black hover:bg-zinc-200" data-testid="rename-save">Save</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete dialog */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent className="border-white/10 bg-[#0a0a0a] text-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete conversation?</AlertDialogTitle>
            <AlertDialogDescription className="text-white/55">
              This permanently removes "{deleteTarget?.title}" and all of its messages.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-white/15 text-white hover:bg-white/5">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-500 text-white hover:bg-red-600" data-testid="delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Tabs>
  );
}

function EmptyHero({ conversationTitle, onPrompt, onNew }) {
  return (
    <div className="flex h-full flex-col items-center justify-center px-6 py-12 text-center">
      <motion.div
        initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
        className="flex h-14 w-14 items-center justify-center rounded-2xl brand-bg"
      >
        <Sparkles className="h-6 w-6 text-white" />
      </motion.div>
      <h2 className="font-display mt-5 text-3xl font-light tracking-tight">
        {conversationTitle || "What's on your mind?"}
      </h2>
      <p className="mt-2 max-w-md text-sm text-white/55">
        Ask anything — drafting, summarising, analysing. Toggle the knowledge base to ground the
        model in your own documents.
      </p>
      {onNew && (
        <Button onClick={onNew} className="mt-6 rounded-full bg-white text-black hover:bg-zinc-200" data-testid="empty-state-new">
          <Plus className="mr-1 h-4 w-4" /> Start a conversation
        </Button>
      )}
      <div className="mt-8 grid w-full max-w-2xl grid-cols-1 gap-3 sm:grid-cols-2">
        {STARTER_PROMPTS.map((p, i) => (
          <button
            key={i}
            onClick={() => onPrompt(p.text)}
            className="rounded-xl border border-white/10 bg-white/[0.02] p-4 text-left text-sm text-white/80 transition hover:border-white/20 hover:bg-white/[0.05]"
            data-testid={`starter-prompt-${i}`}
          >
            <div className="font-display text-base text-white">{p.title}</div>
            <div className="mt-1 text-xs text-white/50 line-clamp-2">{p.text}</div>
          </button>
        ))}
      </div>
    </div>
  );
}
